import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { AgentService } from "../../services/AgentService";
import { ApplicationService } from "../../services/ApplicationService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { PlusIcon } from "lucide-react";
import AgentsTable, { FilterConfig } from "./AgentsTable";

interface AgentWithApplication extends Agent {
  application_name?: string;
}

interface SortConfig {
  key: keyof AgentWithApplication | null;
  direction: "asc" | "desc";
}

const Agents: React.FC = () => {
  const { apiKey } = usePermissions();
  const [agents, setAgents] = useState<AgentWithApplication[]>([]);
  const [allAgents, setAllAgents] = useState<AgentWithApplication[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [totalAgents, setTotalAgents] = useState<number>(0);
  const [loadingState, setLoadingState] = useState<"loading" | "loaded" | "error">("loading");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<number | null>(null);
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({
    applicationId: null,
    includeSubagents: false,
  });
  const pageSize = 10;

  const navigate = useNavigate();

  // Load agents function
  const loadAgents = React.useCallback(() => {
    if (apiKey) {
      setLoadingState("loading");
      const agentSubscription = AgentService.getAgents(() => {}, apiKey, filterConfig.includeSubagents).subscribe(
        (response: any) => {
          const fetchedAgents = Array.isArray(response) ? response : (response.agents || []);
          setAllAgents(fetchedAgents);
          setLoadingState("loaded");
        },
        (error: any) => {
          console.error("Error loading agents:", error);
          setLoadingState("error");
        }
      );
      return agentSubscription;
    }
  }, [apiKey, filterConfig.includeSubagents]);

  // Load agents when apiKey or includeSubagents filter changes
  useEffect(() => {
    const subscription = loadAgents();

    // Cleanup subscription on effect cleanup or component unmount
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [loadAgents]);

  // Listen for agent updates from assistant
  useEffect(() => {
    let updateSubscription: any = null;

    const handleAgentUpdate = (event: any) => {
      console.log("Agent updated event received:", event.detail);
      // Cancel any pending subscription before starting a new one
      if (updateSubscription) {
        updateSubscription.unsubscribe();
      }
      // Reload agents when assistant makes changes
      updateSubscription = loadAgents();
    };

    window.addEventListener("agentUpdated", handleAgentUpdate);

    return () => {
      window.removeEventListener("agentUpdated", handleAgentUpdate);
      // Clean up any pending subscription on unmount
      if (updateSubscription) {
        updateSubscription.unsubscribe();
      }
    };
  }, [loadAgents]);

  // Load applications only when apiKey changes
  useEffect(() => {
    let applicationSubscription: any = null;

    if (apiKey) {
      applicationSubscription = ApplicationService.getApplications(() => {}, apiKey).subscribe(
        (response: any) => {
          const fetchedApplications = Array.isArray(response) ? response : (response.applications || []);
          setApplications(fetchedApplications);
        },
        (error: any) => {
          console.error("Error loading applications:", error);
        }
      );
    }

    // Cleanup subscription on effect cleanup or component unmount
    return () => {
      if (applicationSubscription) {
        applicationSubscription.unsubscribe();
      }
    };
  }, [apiKey]);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterConfig]);

  // Filter and sort agents based on current filter and sort config
  const filteredAndSortedAgents = React.useMemo(() => {
    if (!allAgents.length) return [];
    
    // First, apply filters
    let filteredAgents = [...allAgents];
    
    // Filter by application if selected
    if (filterConfig.applicationId) {
      filteredAgents = filteredAgents.filter(
        (agent) => String(agent.application_id) === filterConfig.applicationId
      );
    }
    
    // Note: includeSubagents filtering is now handled at the backend level.
    // When includeSubagents is false, the backend excludes agents that exist
    // as child_agent_id in the agent_subagent table.
    
    // Then sort the filtered agents
    if (sortConfig.key) {
      filteredAgents.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === "asc" ? 1 : -1;
        if (bValue == null) return sortConfig.direction === "asc" ? -1 : 1;
        
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Default sort by date_created descending
      filteredAgents.sort((a, b) => {
        const dateA = a.date_created && a.date_created > 0 ? a.date_created : 0;
        const dateB = b.date_created && b.date_created > 0 ? b.date_created : 0;
        return dateB - dateA;
      });
    }
    
    return filteredAgents;
  }, [allAgents, sortConfig, filterConfig]);

  // Update total count when filtered agents change
  useEffect(() => {
    setTotalAgents(filteredAndSortedAgents.length);
  }, [filteredAndSortedAgents.length]);

  // Paginate the filtered and sorted agents
  const sortedAndPaginatedAgents = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedAgents.slice(startIndex, endIndex);
  }, [filteredAndSortedAgents, currentPage, pageSize]);

  // Update displayed agents when sorting or pagination changes
  React.useEffect(() => {
    setAgents(sortedAndPaginatedAgents);
  }, [sortedAndPaginatedAgents]);



  const handleClearFilters = () => {
    setFilterConfig({
      applicationId: null,
      includeSubagents: false,
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handleDelete = (agentId: number) => {
    if (!apiKey) return;
    
    setLoadingState("loading");
    AgentService.deleteAgent(agentId, () => {}, apiKey).subscribe(
      () => {
        // Update both the full list and the displayed agents
        setAllAgents((prevAgents) =>
          prevAgents.filter((agent) => agent.id !== agentId)
        );
        setAgents((prevAgents) =>
          prevAgents.filter((agent) => agent.id !== agentId)
        );
        setTotalAgents((prevTotal) => prevTotal - 1);
        setDeleteDialogOpen(null);
        setLoadingState("loaded");
      },
      (error: any) => {
        console.error("Error deleting agent:", error);
        setLoadingState("error");
      }
    );
  };

  const handleClone = (agentId: number, newAgentName: string, newApplicationName: string) => {
    if (!apiKey) return;
    
    setLoadingState("loading");
    AgentService.cloneAgent(agentId, newAgentName, newApplicationName, (loading: boolean) => {
      if (loading) setLoadingState("loading");
    }, apiKey).subscribe(
      (response: any) => {
        // Refresh the agent list to include the newly cloned agent
        AgentService.getAgents((loading: boolean) => {
          if (loading) setLoadingState("loading");
        }, apiKey, filterConfig.includeSubagents).subscribe(
          (agentsResponse: any) => {
            const fetchedAgents = Array.isArray(agentsResponse) ? agentsResponse : (agentsResponse.agents || []);
            setAllAgents(fetchedAgents);
            setLoadingState("loaded");
          },
          (error: any) => {
            console.error("Error refreshing agents after clone:", error);
            setLoadingState("error");
          }
        );
      },
      (error: any) => {
        console.error("Error cloning agent:", error);
        setLoadingState("error");
      }
    );
  };

  const handleCreateAgent = () => {
    navigate("/agent/new");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Agents</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Agents
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Manage your AI agents and their configurations
                </p>
              </div>
              <Button onClick={handleCreateAgent} className="gap-2">
                <PlusIcon className="w-4 h-4" />
                Create Agent
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Agents Table */}
        <Card className="border-border">
          <CardContent className="p-0">
            <AgentsTable
              agents={agents}
              isLoading={loadingState === "loading"}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              sortConfig={sortConfig}
              setSortConfig={setSortConfig}
              onDelete={handleDelete}
              onClone={handleClone}
              onCreateAgent={handleCreateAgent}
              deleteDialogOpen={deleteDialogOpen}
              setDeleteDialogOpen={setDeleteDialogOpen}
              pageSize={pageSize}
              totalAgents={totalAgents}
              applications={applications}
              filterConfig={filterConfig}
              setFilterConfig={setFilterConfig}
              onClearFilters={handleClearFilters}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Agents;
